import { useDispatch } from 'react-redux'
import { useEffect, useState, useRef, useCallback } from 'react'

import { useAppSelector } from '../../app/hooks'
import { STORAGE_URL } from '../../shared/constants/env'
import { Map, MapsConfig, ZoneTypeEnum } from '../../shared/types'
import { pixelToGeo } from '../../shared/utils/pixelToGeo.utils'
import { updateLocationIfMoved } from '../../features/userLocation/userLocationSlice'
import {
  getMockUserCoordsStorage,
  setMockUserCoordsStorage,
} from '../../shared/utils/loadMockUserCoords.utils'

const useMap = () => {
  const dispatch = useDispatch()

  const [mapsConfig, setMapsConfig] = useState<MapsConfig>()
  const [map, setMap] = useState<Map>()

  const [mapImg, setMapImg] = useState<string | null>(null)
  const [imgSize, setImgSize] = useState({ width: 0, height: 0 })
  const imgRef = useRef<HTMLImageElement>(null)

  // =======================
  // Selectors
  // =======================
  const {
    activeMapId,
    visitedMaps: mapsData,
    loading: mapLoading,
    error: mapError,
  } = useAppSelector((state) => state.maps)
  const {
    data: zoneData,
    loading: zoneLoading,
    error: zoneError,
  } = useAppSelector((state) => state.zone)
  const activeZoneId = zoneData?.id
  const { overridden: overrideGeoLoc } = useAppSelector(
    (state) => state.userLocation
  )

  const mockLSCoords = getMockUserCoordsStorage()
  const allowUserClick =
    (overrideGeoLoc && process.env.REACT_APP_CLICK_USER_LOC === 'true') ||
    (mockLSCoords?.latitude && mockLSCoords?.longitude)

  // =======================
  // Functions
  // =======================

  // Track image size
  const updateSize = useCallback(() => {
    const img = imgRef.current
    if (img) {
      setImgSize({
        width: img.offsetWidth,
        height: img.offsetHeight,
      })
    }
  }, [])

  // =======================
  // Effects
  // =======================

  // Set map config
  useEffect(() => {
    if (zoneData?.type === ZoneTypeEnum.Case && zoneData?.id && mapsData) {
      setMapsConfig(zoneData?.mapsConfig)
    }
  }, [zoneData, mapsData])

  // Set map and image path
  useEffect(() => {
    // const mapZone = mapsConfig?.maps?.zone
    if (zoneData && activeMapId && mapsData?.[activeMapId]) {
      const activeMap = (mapsData as Record<string, Map>)[activeMapId]
      setMap({
        ...activeMap,
        // boundingBox: mapZone?.boundingBox || activeZone?.boundingBox,
      })
      setMapImg(`${STORAGE_URL}zones/${zoneData.id}/maps/${activeMapId}.jpg`)
    } else {
      setMap(undefined)
      setMapImg(null)
    }
  }, [mapsConfig, zoneData, activeMapId, mapsData])

  useEffect(() => {
    if (!mapImg || !imgRef.current) return

    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [mapImg, updateSize])

  // =======================
  // Handlers
  // =======================

  const handleMapClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!allowUserClick || !map?.boundingBox || !imgRef.current) return

    const rect = imgRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    const { lat, long } = pixelToGeo(
      x,
      y,
      map.boundingBox,
      imgSize.width,
      imgSize.height
    )

    // update localstorage: persist local if using LS
    const coords = getMockUserCoordsStorage()
    if (coords?.latitude && coords?.longitude) {
      const coords = {
        latitude: Number(lat),
        longitude: Number(long),
      }
      setMockUserCoordsStorage(coords)
    }

    dispatch(
      updateLocationIfMoved({
        lat,
        long,
        accuracy: 0,
        timestamp: Date.now(),
      })
    )
  }

  return {
    map,
    mapImg,
    imgRef,
    imgSize,
    updateSize,
    allowUserClick,
    loading: mapLoading || zoneLoading || !mapImg,
    error: mapError,
    handlers: {
      handleMapClick,
    },
  }
}

export default useMap
